import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { JourneyProgress } from "@/components/JourneyProgress";
import { JoinJourneyCard } from "@/components/JoinJourneyCard";
import { ShareJourney } from "@/components/ShareJourney";
import { fetchJournalEntries, sortOldestFirst, JOURNAL_FALLBACK } from "@/lib/journal";
import { findStageBySlug } from "@/lib/journey";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
};

export default function JournalEntry() {
  const { slug } = useParams();
  const [entries, setEntries] = useState(() => sortOldestFirst(JOURNAL_FALLBACK));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetchJournalEntries()
      .then((data) => {
        if (mounted && Array.isArray(data) && data.length) setEntries(sortOldestFirst(data));
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const { entry, prev, next } = useMemo(() => {
    const idx = entries.findIndex((e) => e.slug === slug);
    if (idx === -1) return { entry: null, prev: null, next: null };
    return {
      entry: entries[idx],
      prev: idx > 0 ? entries[idx - 1] : null,
      next: idx < entries.length - 1 ? entries[idx + 1] : null,
    };
  }, [entries, slug]);

  useEffect(() => {
    document.title = entry ? `ROADRIG · ${entry.title}` : "ROADRIG · Journal";
  }, [entry]);

  const stage = findStageBySlug(slug);

  if (!loading && !entry) {
    return (
      <div data-testid="journal-entry-notfound" className="relative bg-[#0A0A0C] text-white min-h-screen">
        <Navbar />
        <section className="pt-44 pb-24 max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="serial text-[10px] text-[#F47A20] mb-4">ENTRY NOT FOUND</div>
          <h1 className="h-display text-white text-4xl sm:text-5xl mb-6">This chapter isn&apos;t written yet.</h1>
          <p className="text-white/65 mb-10">The journal entry you&apos;re looking for doesn&apos;t exist. Head back to the archive to read the full build story.</p>
          <Link to="/journal" data-testid="journal-entry-back-to-archive" className="btn-primary inline-flex">
            <ArrowLeft size={16} /> Back to Journal Archive
          </Link>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="journal-entry-page" data-slug={slug} className="relative bg-[#0A0A0C] text-white min-h-screen">
      <Navbar />

      {/* Header + journey progress */}
      <section className="relative pt-32 md:pt-40 pb-10 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
            <Link to="/journal" data-testid="journal-entry-back" className="serial text-[10px] text-white/55 hover:text-white inline-flex items-center gap-2">
              <ArrowLeft size={12} /> Full Archive
            </Link>
            <Link to="/" className="serial text-[10px] text-white/40 hover:text-white">
              ROADRIG.COM.AU
            </Link>
          </div>

          <JourneyProgress currentSlug={slug} />
        </div>
      </section>

      {entry && (
        <article className="py-14 md:py-20">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            {/* Meta */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
              className="flex items-center gap-3 mb-5 serial text-[10px] text-white/55"
            >
              <span className="text-[#F47A20] font-bold tracking-[0.25em]">{entry.tag}</span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span className="text-[#F47A20] tracking-[0.25em]">{(entry.date_label || "").toUpperCase()}</span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span className="inline-flex items-center gap-1.5"><Calendar size={11} /> {fmtDate(entry.published_at)}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              data-testid="journal-entry-title"
              className="h-display text-white text-4xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight mb-10"
            >
              {entry.title}
            </motion.h1>

            {/* Hero media */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="relative rounded-sm overflow-hidden border border-white/10 bg-[#141417] mb-12"
              data-testid="journal-entry-hero"
            >
              {entry.video_url ? (
                <video
                  controls
                  preload="none"
                  poster={entry.image_url}
                  className="w-full h-auto block max-h-[78vh] object-cover"
                >
                  <source src={entry.video_url} />
                </video>
              ) : (
                <img
                  src={entry.image_url}
                  alt={entry.date_label}
                  style={{ objectPosition: entry.object_pos || "50% 50%" }}
                  className="w-full h-auto block max-h-[78vh] object-cover"
                />
              )}
              {stage && (
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-2.5 py-1 bg-[#0A0A0C]/80 backdrop-blur border border-[#F47A20]/35 rounded-sm">
                  <span className="serial text-[9px] tracking-[0.25em] text-[#F47A20] font-bold">
                    PHASE {String(stage.idx).padStart(2, "0")} &middot; {stage.shortLabel.toUpperCase()}
                  </span>
                </div>
              )}
            </motion.div>

            {/* Body */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <p data-testid="journal-entry-body" className="text-white/82 text-lg md:text-xl leading-[1.75] whitespace-pre-line">
                {entry.body}
              </p>

              <div className="mt-12 pt-8 border-t border-white/8 serial text-[10px] text-white/45">
                Built by riders. Designed for the journey. &mdash; John, ROADRIG
              </div>
            </motion.div>

            {/* Join the Journey CTA — appears on every entry */}
            <div className="mt-16 md:mt-20">
              <JoinJourneyCard source={`journal-entry:${slug}`} />
            </div>

            {/* Share the Journey — recognition-based, no rewards/discounts */}
            <div className="mt-10">
              <ShareJourney
                url={typeof window !== "undefined" ? window.location.origin + `/journal/${slug}` : ""}
                title={entry?.title}
                description={entry?.body?.slice(0, 180)}
              />
            </div>
          </div>
        </article>
      )}

      {/* Prev / Next */}
      {(prev || next) && (
        <section className="pb-20 md:pb-28">
          <div className="max-w-5xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            {prev ? (
              <Link
                to={`/journal/${prev.slug}`}
                data-testid="journal-entry-prev"
                className="group p-5 border border-white/10 hover:border-[#F47A20]/55 transition-colors rounded-sm bg-[#0E0E11]"
              >
                <div className="serial text-[10px] tracking-[0.25em] text-white/45 mb-2 inline-flex items-center gap-1.5">
                  <ArrowLeft size={11} /> PREVIOUS &middot; {prev.tag}
                </div>
                <div className="text-white font-bold text-lg group-hover:text-[#F47A20] transition-colors">{prev.title}</div>
              </Link>
            ) : <div />}
            {next ? (
              <Link
                to={`/journal/${next.slug}`}
                data-testid="journal-entry-next"
                className="group p-5 border border-white/10 hover:border-[#F47A20]/55 transition-colors rounded-sm bg-[#0E0E11] md:text-right"
              >
                <div className="serial text-[10px] tracking-[0.25em] text-white/45 mb-2 inline-flex md:justify-end items-center gap-1.5 w-full">
                  NEXT &middot; {next.tag} <ArrowRight size={11} />
                </div>
                <div className="text-white font-bold text-lg group-hover:text-[#F47A20] transition-colors">{next.title}</div>
              </Link>
            ) : <div />}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
